import { createAsyncThunk } from '@reduxjs/toolkit';
import QuotesResource from 'api/service/QuotesResource';
import FilesServiceResource from 'api/service/FilesResource';
import { convertToBase64 } from 'utils/file-upload';

export const uploadAttachmentMeta = createAsyncThunk(
  'marketplace/uploadAttachmentMeta',
  async (initialData, { rejectWithValue }) => {
    const { quoteId, file, itemId } = initialData;
    const quotesResource = new QuotesResource();
    const { status, data } = await quotesResource.quote(quoteId).attachments().upload(file, itemId);
    if (status === 200) {
      return {
        status,
        data: { ...data, content_type: file.type, size: file.size, fileUrl: await convertToBase64(file) },
      };
    }
    return rejectWithValue('upload attachment metadata failed');
  }
);

export const uploadAttachmentBlob = createAsyncThunk(
  'marketplace/uploadAttachmentBlob',
  async (initialData, { rejectWithValue }) => {
    const { id, uploadUrl, file } = initialData;
    const hasExceedMaxAttachmentSize = file.size > 10000000;
    if (hasExceedMaxAttachmentSize) {
      return rejectWithValue(id);
    }
    const quotesResource = new QuotesResource();
    const { status, data } = await quotesResource.uploadAttachment(uploadUrl, file);
    if (status === 201) {
      return { status, data, id };
    }
    return rejectWithValue(id);
  }
);

export const uploadAttachmentRefresh = createAsyncThunk(
  'marketplace/uploadAttachmentRefresh',
  async (initialData, { rejectWithValue }) => {
    const { id, oid } = initialData;
    const filesResource = new FilesServiceResource();
    const { status, data } = await filesResource.file(oid).refresh();
    if (status === 200) {
      return { id, status, data };
    }
    return rejectWithValue(id);
  }
);

export const uploadQuoteAttachments = createAsyncThunk(
  'marketplace/uploadQuoteAttachments',
  async (initialData, { dispatch }) => {
    const { quoteId, files, itemId } = initialData;

    if (!files || files.length === 0) {
      console.warn('No files provided for upload.');
      return;
    }

    const uploadPromises = Array.from(files).map(async (file) => {
      // Step 1: Upload file metadata
      const { payload: uploadMetaRes } = await dispatch(uploadAttachmentMeta({ quoteId, file, itemId }));

      if (uploadMetaRes?.status === 200) {
        // Step 2: Upload the actual file to the blob/storage URL
        const { payload: uploadBlobRes } = await dispatch(
          uploadAttachmentBlob({
            id: uploadMetaRes.data.id,
            uploadUrl: uploadMetaRes.data.upload_url,
            file,
          })
        );

        if (uploadBlobRes?.status === 201) {
          // Step 3: Refresh the file to get final details
          const { payload: uploadRefreshRes } = await dispatch(
            uploadAttachmentRefresh({
              id: uploadMetaRes.data.id,
              oid: uploadMetaRes.data.oid,
            })
          );
          if (uploadRefreshRes?.status === 200) {
            return uploadRefreshRes.data;
          }
          return 'uploaded attachment refresh failed';
        }
        return 'upload attachment blob failed';
      }
      return 'upload attachment metadata failed';
    });

    // Wait for all uploads to finish
    const uploadedAttachments = await Promise.all(uploadPromises);

    return uploadedAttachments;
  }
);
